import React, { useEffect, useMemo, useState } from "react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  BarChart, Bar, Cell,
} from "recharts";
import { api, scopeParams } from "../lib/api";
import { useSession } from "../context/SessionContext";
import KpiCard from "../components/KpiCard";
import ModuleHero from "../components/ModuleHero";
import SizedChart from "../components/SizedChart";
import { fmtInr, fmtPct, shortDate, fmtShare } from "../lib/format";

const PERIODS = [
  { id: 14, label: "14D" },
  { id: 30, label: "30D" },
  { id: 90, label: "90D" },
  { id: 365, label: "1Y" },
  { id: 730, label: "2Y" },
];

const Sales = () => {
  const { session, focusBranchId } = useSession();
  const [period, setPeriod] = useState(90);
  const [brandFilter, setBrandFilter] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    api
      .get("/sales", { params: { ...scopeParams(session, focusBranchId), period, brand_filter: brandFilter } })
      .then((r) => mounted && setData(r.data))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [session, focusBranchId, period, brandFilter]);

  const s = data || {};

  // Derive a 30-day spark trend for the hero from the daily revenue trend already returned.
  const heroSpark = useMemo(() => {
    const tr = s.revenue_trend || [];
    return tr.slice(-30).map((p) => Number(p.revenue || 0));
  }, [s]);

  return (
    <div className="space-y-6" data-testid="sales-page">
      {/* Module hero */}
      {!loading && s.trend_summary && (
        <ModuleHero
          testId="sales-hero"
          eyebrow="Revenue Snapshot"
          title="How the chain is selling right now"
          subtitle={`Rolling 7-day revenue across ${s.scope_label || "your scope"}, year-on-year context below.`}
          primary={{
            label: "Last 7 days",
            value: s.trend_summary.last7_revenue || 0,
            countFormatter: (n) => fmtInr(n, { compact: true }),
            delta: s.trend_summary.wow_delta_pct,
          }}
          secondaries={[
            {
              label: "YoY · 30D",
              value: fmtPct(s.trend_summary.yoy_delta_pct || 0),
              accent: (s.trend_summary.yoy_delta_pct || 0) >= 0 ? "green" : "red",
            },
            {
              label: "Best branch · wk",
              value: s.best_worst?.best?.name || "—",
              accent: "green",
            },
          ]}
          spark={heroSpark}
          accent="teal"
        />
      )}

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          testId="sales-kpi-last7"
          label="Last 7 Days"
          countTarget={loading ? 0 : s.trend_summary?.last7_revenue}
          countFormatter={(n) => fmtInr(n, { compact: true })}
          delta={s.trend_summary?.wow_delta_pct}
          spark={heroSpark}
          hint="Week-over-week"
        />
        <KpiCard
          testId="sales-kpi-yoy"
          label="Last 30 Days · YoY"
          countTarget={loading ? 0 : s.trend_summary?.yoy_delta_pct}
          countDecimals={1}
          countFormatter={(n) => `${n >= 0 ? "+" : ""}${n.toFixed(1)}%`}
          hint="Same 30-day window, prior year"
          tone={s.trend_summary?.yoy_delta_pct < 0 ? "warn" : "success"}
        />
        <KpiCard
          testId="sales-kpi-best"
          label="Best Branch · Wk"
          value={loading ? "—" : (s.best_worst?.best?.name || "—")}
          delta={s.best_worst?.best?.wow_delta_pct}
          hint={s.best_worst?.best ? `${fmtInr(s.best_worst.best.last7_revenue, { compact: true })} in last 7 days` : ""}
          tone="success"
        />
        <KpiCard
          testId="sales-kpi-worst"
          label="Worst Branch · Wk"
          value={loading ? "—" : (s.best_worst?.worst?.name || "—")}
          delta={s.best_worst?.worst?.wow_delta_pct}
          hint={s.best_worst?.worst ? `${fmtInr(s.best_worst.worst.last7_revenue, { compact: true })} in last 7 days` : ""}
          tone="warn"
        />
      </div>

      {/* Revenue trend */}
      <section className="ang-panel">
        <header className="flex items-center justify-between border-b border-ang px-6 py-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-ang-muted">Revenue Trend</div>
            <h3 className="font-serif text-xl mt-1">Daily revenue · {s.scope_label || "…"}</h3>
          </div>
          <div className="flex gap-1" data-testid="period-tabs">
            {PERIODS.map((p) => (
              <button
                key={p.id}
                data-testid={`period-${p.id}`}
                onClick={() => setPeriod(p.id)}
                className={`text-[11px] uppercase tracking-widest px-2.5 py-1 border transition-colors ${
                  period === p.id ? "bg-[#14142B] text-white border-[#14142B]" : "bg-white border-ang hover:border-[#14142B]"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </header>
        <SizedChart height={300}>
            <LineChart data={s.revenue_trend || []} margin={{ top: 10, right: 30, left: 10, bottom: 0 }}>
              <CartesianGrid stroke="#E7E4DC" strokeDasharray="3 4" vertical={false} />
              <XAxis dataKey="date" tickFormatter={shortDate} minTickGap={40} axisLine={false} tickLine={false} />
              <YAxis
                tickFormatter={(v) => (v >= 1e7 ? `${(v/1e7).toFixed(1)}Cr` : v >= 1e5 ? `${(v/1e5).toFixed(1)}L` : v >= 1e3 ? `${(v/1e3).toFixed(0)}K` : v)}
                axisLine={false} tickLine={false} width={70}
              />
              <Tooltip
                labelFormatter={shortDate}
                formatter={(v) => [fmtInr(v), "Revenue"]}
              />
              <Line type="monotone" dataKey="revenue" stroke="#14142B" strokeWidth={1.6} dot={false} activeDot={{ r: 4, fill: "#C9A15C" }} />
            </LineChart>
          </SizedChart>
      </section>

      {/* Two-column: Top categories & Brand split */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <section className="ang-panel">
          <header className="border-b border-ang px-6 py-4">
            <div className="text-[10px] uppercase tracking-widest text-ang-muted">Top Categories · 30D</div>
            <h3 className="font-serif text-xl mt-1">Revenue by category</h3>
          </header>
          <table className="w-full text-sm" data-testid="top-categories">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-ang-muted border-b border-ang">
                <th className="text-left px-6 py-3">Category</th>
                <th className="text-right px-6 py-3">Revenue</th>
                <th className="text-right px-6 py-3 w-32">Share</th>
              </tr>
            </thead>
            <tbody>
              {(s.top_categories || []).map((c, i) => (
                <tr key={c.category} className={`border-b border-ang ${i % 2 === 1 ? "bg-[#FAF9F6]/60" : ""}`}>
                  <td className="px-6 py-2.5">{c.category}</td>
                  <td className="px-6 py-2.5 text-right tabular-nums">{fmtInr(c.revenue, { compact: true })}</td>
                  <td className="px-6 py-2.5 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <div className="w-20 h-1.5 bg-[#E7E4DC] relative">
                        <div className="absolute inset-y-0 left-0" style={{ background: "#14142B", width: `${c.share * 100}%` }} />
                      </div>
                      <span className="tabular-nums text-xs w-12 text-right">{fmtShare(c.share)}</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        <section className="ang-panel">
          <header className="flex items-center justify-between border-b border-ang px-6 py-4">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-ang-muted">Brand Split · 30D</div>
              <h3 className="font-serif text-xl mt-1">Private label vs. branded</h3>
            </div>
            <div className="flex gap-1" data-testid="brand-filter">
              {[{id:"all",l:"All"},{id:"private",l:"Own label only"},{id:"branded",l:"Branded only"}].map((b) => (
                <button
                  key={b.id}
                  data-testid={`brand-filter-${b.id}`}
                  onClick={() => setBrandFilter(b.id)}
                  className={`text-[10px] uppercase tracking-widest px-2 py-1 border transition-colors ${
                    brandFilter === b.id ? "bg-[#14142B] text-white border-[#14142B]" : "bg-white border-ang hover:border-[#14142B]"
                  }`}
                >
                  {b.l}
                </button>
              ))}
            </div>
          </header>
          <div className="p-6 space-y-6">
            <div>
              <div className="flex items-baseline justify-between mb-2">
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-ang-muted">Own label</div>
                  <div className="font-serif text-2xl tabular-nums" style={{ color: "#C9A15C" }} data-testid="brand-private-value">
                    {fmtInr(s.brand_split?.private_label_revenue, { compact: true })}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[11px] uppercase tracking-widest text-ang-muted">Share</div>
                  <div className="font-serif text-2xl tabular-nums">{s.brand_split ? fmtShare(s.brand_split.private_label_share) : "—"}</div>
                </div>
              </div>
              <div className="h-3 bg-[#E7E4DC] relative">
                <div
                  className="absolute inset-y-0 left-0"
                  style={{ background: "#C9A15C", width: `${(s.brand_split?.private_label_share || 0) * 100}%` }}
                />
              </div>
              <div className="mt-3 flex items-baseline justify-between">
                <div className="text-[11px] uppercase tracking-widest text-ang-muted">External brands</div>
                <div className="tabular-nums text-sm">{fmtInr(s.brand_split?.branded_revenue, { compact: true })}</div>
              </div>
            </div>
            <div className="pt-4 border-t border-ang text-xs text-ang-muted">
              Contribution measured across Dairy, Groceries & Pulses, Fresh Bakery and Gift Items — the four categories where the chain runs an own-label brand.
            </div>
          </div>
        </section>
      </div>

      {/* Hourly footfall */}
      <section className="ang-panel">
        <header className="border-b border-ang px-6 py-4">
          <div className="text-[10px] uppercase tracking-widest text-ang-muted">Footfall & Basket · Today</div>
          <h3 className="font-serif text-xl mt-1">By hour of day</h3>
        </header>
        <SizedChart height={280}>
            <BarChart data={s.hourly_footfall || []} margin={{ top: 10, right: 30, left: 10, bottom: 0 }}>
              <CartesianGrid stroke="#E7E4DC" strokeDasharray="3 4" vertical={false} />
              <XAxis dataKey="hour" tickFormatter={(h) => `${h}:00`} axisLine={false} tickLine={false} />
              <YAxis yAxisId="l" axisLine={false} tickLine={false} width={60} />
              <YAxis yAxisId="r" orientation="right" axisLine={false} tickLine={false} width={70} tickFormatter={(v) => `₹${v}`} />
              <Tooltip formatter={(v, n) => n === "footfall" ? [v, "Footfall"] : [`₹${v}`, "Avg. Basket"]} labelFormatter={(h) => `${h}:00`} />
              <Bar yAxisId="l" dataKey="footfall" name="footfall" fill="#14142B" radius={[0,0,0,0]}>
                {(s.hourly_footfall || []).map((_, i) => <Cell key={i} />)}
              </Bar>
              <Line yAxisId="r" type="monotone" dataKey="avg_basket" name="avg_basket" stroke="#C9A15C" strokeWidth={1.6} dot={false} />
            </BarChart>
          </SizedChart>
      </section>
    </div>
  );
};

export default Sales;
